import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "@/lib/api";
import { CHAPTERS } from "@/lib/guide";
import { useAuth } from "@/context/AuthContext";
import { useRemanso } from "@/context/RemansoContext";
import { EmotionGuide } from "@/components/EmotionGuide";
import { RemansoStory, Chapter } from "@/components/RemansoStory";

const TITLES = { guia: "Ponerle nombre", historia: "Los Remansos", bienvenida: "Bienvenida" };

const Welcome = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const last = step === CHAPTERS.length - 1;
  return (
    <div className="flex flex-col gap-8" data-testid="welcome">
      <Chapter key={step} ch={CHAPTERS[step]} index={step} />
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-meta" data-testid="welcome-step">{step + 1} de {CHAPTERS.length}</span>
        <div className="flex gap-2">
          {!last && <button type="button" className="btn-text" onClick={() => onDone(false)} data-testid="welcome-skip-button">Saltar</button>}
          {last
            ? <button type="button" className="btn-primary" onClick={() => onDone(true)} data-testid="welcome-start-button">Empezar a escribir</button>
            : <button type="button" className="btn-primary" onClick={() => setStep(step + 1)} data-testid="welcome-next-button">Siguiente</button>}
        </div>
      </div>
    </div>
  );
};

export const InfoPanel = () => {
  const { info, closeInfo } = useRemanso();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const welcome = info?.view === "bienvenida";

  const finishWelcome = (write) => {
    closeInfo();
    setUser((u) => (u ? { ...u, welcome_pending: false } : u));
    api.post("/auth/welcome-seen").catch(() => {});
    if (write) navigate("/escribir");
  };
  const close = () => (welcome ? finishWelcome(false) : closeInfo());

  useEffect(() => {
    if (!info) return undefined;
    const el = scrollRef.current;
    requestAnimationFrame(() => {
      const target = info.anchor && document.getElementById(info.anchor);
      if (target) el.scrollTop = target.offsetTop - 16;
      else el.scrollTop = 0;
    });
    const onKey = (e) => { if (e.key === "Escape") close(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [info]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info || !user) return null;
  return (
    <aside role="dialog" aria-modal="false" aria-label={TITLES[info.view]} data-testid={`info-panel-${info.view}`}
      className="info-panel fixed z-[45] bg-papel text-tinta flex flex-col inset-x-0 bottom-0 max-h-[78vh] rounded-t-[24px] border-t border-linea lg:inset-x-auto lg:right-0 lg:top-0 lg:max-h-none lg:w-[420px] lg:rounded-none lg:border-t-0 lg:border-l">
      <div className="shrink-0 flex items-center justify-between gap-4 px-6 pt-5 pb-3">
        <h2 className="font-serif text-[28px] leading-tight text-tinta" data-testid="info-panel-title">{TITLES[info.view]}</h2>
        <button type="button" className="icon-btn" aria-label="Cerrar" onClick={close} data-testid="info-panel-close">
          <X size={20} strokeWidth={1.75} />
        </button>
      </div>
      <div ref={scrollRef} className="relative flex-1 min-h-0 overflow-y-auto px-6 pb-8 pt-2" data-testid="info-panel-scroll">
        {info.view === "guia" && <EmotionGuide />}
        {info.view === "historia" && <RemansoStory />}
        {welcome && <Welcome onDone={finishWelcome} />}
      </div>
    </aside>
  );
};
